import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Loader2, Plus, X } from 'lucide-react';
import { toast } from 'sonner';
import { formatPrice } from '@/lib/formatters';
import { drivetrainLabels } from '@/types/database';
import type { Tables } from '@/integrations/supabase/types';
import type { FlowField, FlowKind } from '@/constants/contribute-flows';
import { getFlowTitle } from '@/constants/contribute-flows';
import { submitChangeRequest, type ChangeRequestPayload } from '@/lib/submission';

type Variant = Tables<'variants'>;
type Specs = Tables<'powertrain_specs'>;
type Price = Tables<'prices_dk'>;

interface VariantRow {
  variant: Variant;
  specs: Specs | null;
  lowestPrice: Price | null;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  modelId: string;
  brandName: string;
  modelName: string;
  fields: FlowField[];
  flowKind: FlowKind;
  focusedVariantId: string;
  focusedFieldKey?: string | null;
}

type CellValue = string; // alle inputs holdes som strings; konverteres ved submit

export function MultiVariantSuggestDialog({
  open,
  onOpenChange,
  modelId,
  brandName,
  modelName,
  fields,
  flowKind,
  focusedVariantId,
  focusedFieldKey,
}: Props) {
  const { user } = useAuth();
  const focusedColRef = useRef<HTMLTableCellElement | null>(null);

  // ---- Form state ----
  const MAX_SOURCE_URLS = 5;
  const [edits, setEdits] = useState<Record<string, Record<string, CellValue>>>({});
  const [sourceUrls, setSourceUrls] = useState<string[]>(['']);
  const [notes, setNotes] = useState('');
  const [submitterEmail, setSubmitterEmail] = useState('');
  const [submitterName, setSubmitterName] = useState('');
  const [honeypot, setHoneypot] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // ---- Reset on close ----
  useEffect(() => {
    if (!open) {
      setEdits({});
      setSourceUrls(['']);
      setNotes('');
      setSubmitterEmail('');
      setSubmitterName('');
      setHoneypot('');
    }
  }, [open]);

  // ---- Fetch alle varianter for modellen ----
  const { data: rows, isLoading } = useQuery({
    queryKey: ['multi-variant-suggest', modelId],
    enabled: open && !!modelId,
    queryFn: async (): Promise<VariantRow[]> => {
      const { data: variants, error } = await supabase
        .from('variants')
        .select('*')
        .eq('model_id', modelId)
        .eq('is_active', true);
      if (error) throw error;
      const list = variants ?? [];
      if (list.length === 0) return [];

      const ids = list.map(v => v.id);

      const [{ data: specsData }, { data: pricesData }] = await Promise.all([
        supabase.from('powertrain_specs').select('*').in('variant_id', ids),
        supabase.from('prices_dk').select('*').in('variant_id', ids).order('price_dkk', { ascending: true }),
      ]);

      const specsByVariant = new Map<string, Specs>();
      (specsData ?? []).forEach(s => specsByVariant.set(s.variant_id, s));

      const lowestPriceByVariant = new Map<string, Price>();
      (pricesData ?? []).forEach(p => {
        if (!lowestPriceByVariant.has(p.variant_id)) lowestPriceByVariant.set(p.variant_id, p);
      });

      return list.map<VariantRow>(v => ({
        variant: v,
        specs: specsByVariant.get(v.id) ?? null,
        lowestPrice: lowestPriceByVariant.get(v.id) ?? null,
      }));
    },
  });

  // ---- Sortér: laveste pris asc, varianter uden pris til sidst (alfabetisk) ----
  const sortedRows = useMemo(() => {
    if (!rows) return [];
    const withPrice = rows.filter(r => r.lowestPrice).sort((a, b) =>
      (a.lowestPrice!.price_dkk) - (b.lowestPrice!.price_dkk),
    );
    const without = rows.filter(r => !r.lowestPrice).sort((a, b) =>
      a.variant.name.localeCompare(b.variant.name, 'da'),
    );
    return [...withPrice, ...without];
  }, [rows]);

  // ---- Scroll fokuseret kolonne ind i view (kun horisontalt i container, så vi ikke stjæler side-scroll) ----
  useEffect(() => {
    if (!open || !focusedColRef.current) return;
    const el = focusedColRef.current;
    const container = el.closest('[data-variant-scroll]') as HTMLElement | null;
    if (container) {
      const elRect = el.getBoundingClientRect();
      const cRect = container.getBoundingClientRect();
      const offset = (elRect.left - cRect.left) - (cRect.width / 2 - elRect.width / 2);
      container.scrollBy({ left: offset, behavior: 'auto' });
    }
  }, [open, sortedRows, focusedVariantId]);

  // ---- Helpers ----
  const getCurrentValue = (row: VariantRow, field: FlowField): unknown => {
    if (field.scope === 'price') {
      if (field.key === 'price_dkk') return row.lowestPrice?.price_dkk ?? null;
      if (field.key === 'price_type') return row.lowestPrice?.price_type ?? null;
      return null;
    }
    if (field.scope === 'specs') {
      return row.specs ? (row.specs as Record<string, unknown>)[field.key] ?? null : null;
    }
    return (row.variant as Record<string, unknown>)[field.key] ?? null;
  };

  const formatCurrent = (val: unknown, field: FlowField): string => {
    if (val === null || val === undefined || val === '') return '—';
    if (field.type === 'boolean') return val === true ? 'Ja' : val === false ? 'Nej' : '—';
    if (field.key === 'price_dkk' && typeof val === 'number') return formatPrice(val);
    if (field.type === 'select' && field.options) {
      return field.options.find(o => o.value === val)?.label ?? String(val);
    }
    return String(val);
  };

  const setCell = (variantId: string, fieldKey: string, value: string) => {
    setEdits(prev => ({
      ...prev,
      [variantId]: { ...(prev[variantId] ?? {}), [fieldKey]: value },
    }));
  };

  const getCell = (variantId: string, fieldKey: string): string => {
    return edits[variantId]?.[fieldKey] ?? '';
  };

  // ---- Submit: ét kald pr. variant med ændringer ----
  const handleSubmit = async () => {
    if (!user && !submitterEmail.trim()) {
      toast.error('E-mail er påkrævet');
      return;
    }

    // Byg en plan: for hver variant, parse ændrede celler
    type SubmissionPlan = {
      variantId: string;
      variantName: string;
      lowestPrice: Price | null;
      proposedVariantOrSpecs: Record<string, unknown>;
      proposedPrice: { price_dkk?: number; price_type?: string } | null;
    };

    const plans: SubmissionPlan[] = [];

    for (const row of sortedRows) {
      const cellsForVariant = edits[row.variant.id] ?? {};
      const proposedVS: Record<string, unknown> = {};
      const proposedPrice: Record<string, unknown> = {};

      for (const field of fields) {
        const raw = (cellsForVariant[field.key] ?? '').toString().trim();
        if (raw === '') continue;

        let parsed: unknown = raw;
        if (field.type === 'number') {
          const n = parseFloat(raw.replace(',', '.'));
          if (isNaN(n)) {
            toast.error(`Ugyldigt tal for ${row.variant.name} – ${field.label}`);
            return;
          }
          parsed = n;
        } else if (field.type === 'boolean') {
          if (raw === 'ja') parsed = true;
          else if (raw === 'nej') parsed = false;
          else continue;
        }

        // Sammenlign med nuværende værdi for at undgå at sende uændrede
        const current = getCurrentValue(row, field);
        const currentNorm = current === null || current === undefined ? '' : String(current);
        const parsedNorm = String(parsed);
        if (currentNorm === parsedNorm) continue;

        if (field.scope === 'price') {
          proposedPrice[field.key] = parsed;
        } else {
          proposedVS[field.key] = parsed;
        }
      }

      const hasVS = Object.keys(proposedVS).length > 0;
      const hasPrice = Object.keys(proposedPrice).length > 0;
      if (!hasVS && !hasPrice) continue;

      plans.push({
        variantId: row.variant.id,
        variantName: row.variant.name,
        lowestPrice: row.lowestPrice,
        proposedVariantOrSpecs: proposedVS,
        proposedPrice: hasPrice ? proposedPrice : null,
      });
    }

    if (plans.length === 0) {
      toast.error('Ingen ændringer registreret');
      return;
    }

    setSubmitting(true);
    let successCount = 0;
    let failCount = 0;
    const cleanUrls = sourceUrls.map(u => u.trim()).filter(Boolean);
    const combinedSourceUrl = cleanUrls.length > 0 ? cleanUrls.join(', ') : undefined;
    try {
      for (const plan of plans) {
        // Ét forslag pr. variant. Hvis både pris OG variant/specs er ændret,
        // sendes to separate requests for variant.
        const calls: Promise<Awaited<ReturnType<typeof submitChangeRequest>>>[] = [];
        const submit = (formName: string, payload: ChangeRequestPayload) =>
          submitChangeRequest(formName, payload);

        if (Object.keys(plan.proposedVariantOrSpecs).length > 0) {
          calls.push(
            submit('multi_variant_specs', {
                entity_type: 'variant',
                entity_id: plan.variantId,
                proposed_data: plan.proposedVariantOrSpecs,
                source_url: combinedSourceUrl,
                notes: notes || `Multi-variant forslag (${getFlowTitle(flowKind)}): ${plan.variantName}`,
                ...(!user && {
                  submitter_email: submitterEmail,
                  submitter_name: submitterName || undefined,
                }),
                ...(honeypot && { website: honeypot }),
            }),
          );
        }

        if (plan.proposedPrice) {
          // Hvis varianten har en pris, opdater den. Ellers send som expected_price_dkk på varianten.
          if (plan.lowestPrice) {
            calls.push(
              submit('multi_variant_price', {
                  entity_type: 'price_update',
                  entity_id: plan.lowestPrice.id,
                  parent_model_id: plan.variantId,
                  proposed_data: {
                    ...plan.proposedPrice,
                    source_note: notes || null,
                  },
                  source_url: combinedSourceUrl,
                  notes: notes || `Multi-variant prisforslag: ${plan.variantName}`,
                  ...(!user && {
                    submitter_email: submitterEmail,
                    submitter_name: submitterName || undefined,
                  }),
                  ...(honeypot && { website: honeypot }),
              }),
            );
          } else if (plan.proposedPrice.price_dkk !== undefined) {
            calls.push(
              submit('multi_variant_expected_price', {
                  entity_type: 'variant',
                  entity_id: plan.variantId,
                  proposed_data: { expected_price_dkk: plan.proposedPrice.price_dkk },
                  source_url: combinedSourceUrl,
                  notes: notes || `Forslag om ny pris (variant uden pris): ${plan.variantName}`,
                  ...(!user && {
                    submitter_email: submitterEmail,
                    submitter_name: submitterName || undefined,
                  }),
                  ...(honeypot && { website: honeypot }),
              }),
            );
          }
        }

        const results = await Promise.allSettled(calls);
        const allOk = results.every(r => {
          if (r.status === 'rejected') return false;
          return r.value.ok;
        });
        if (allOk) successCount++;
        else failCount++;
      }

      if (successCount > 0 && failCount === 0) {
        toast.success(
          `${successCount} ${successCount === 1 ? 'forslag' : 'forslag'} sendt til godkendelse.`,
        );
        onOpenChange(false);
      } else if (successCount > 0 && failCount > 0) {
        toast.warning(`${successCount} forslag sendt, ${failCount} fejlede.`);
      } else {
        toast.error('Ingen forslag kunne sendes. Prøv igen.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const editedVariantCount = Object.keys(edits).filter(vid =>
    Object.values(edits[vid] ?? {}).some(v => v.toString().trim() !== ''),
  ).length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[min(96vw,1200px)] max-h-[92vh] overflow-hidden flex flex-col p-0">
        <DialogHeader className="px-6 pt-6 pb-2">
          <DialogTitle>{getFlowTitle(flowKind)}</DialogTitle>
          <DialogDescription>
            {brandName} {modelName} — udfyld kun de celler du har data for. Tomme celler ignoreres. Et admin
            gennemgår hvert forslag før offentliggørelse.
          </DialogDescription>
        </DialogHeader>

        {/* Tabel-område */}
        <div data-variant-scroll className="flex-1 overflow-auto border-y bg-muted/20">
          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : sortedRows.length === 0 ? (
            <div className="text-center py-12 text-sm text-muted-foreground">
              Ingen varianter fundet.
            </div>
          ) : (
            <table className="w-full border-collapse text-sm">
              <thead className="sticky top-0 z-20 bg-background">
                <tr>
                  <th className="sticky left-0 z-30 bg-background border-b border-r px-3 py-2 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide min-w-[180px]">
                    Felt
                  </th>
                  {sortedRows.map(row => {
                    const isFocused = row.variant.id === focusedVariantId;
                    return (
                      <th
                        key={row.variant.id}
                        ref={isFocused ? focusedColRef : null}
                        className={`border-b border-r px-3 py-2 text-left align-top min-w-[200px] ${
                          isFocused ? 'bg-primary/5' : 'bg-background'
                        }`}
                      >
                        <div className="flex flex-col gap-1.5">
                          <div className="font-semibold text-foreground leading-tight">
                            {row.variant.name}
                            {isFocused && (
                              <Badge variant="secondary" className="ml-2 text-[10px] py-0 px-1.5 h-4">
                                Valgt
                              </Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-1.5 text-xs">
                            <Badge variant="outline" className="text-[10px] py-0 px-1.5 h-4">
                              {drivetrainLabels[row.variant.drivetrain]}
                            </Badge>
                            <span className="text-muted-foreground">
                              {row.lowestPrice ? formatPrice(row.lowestPrice.price_dkk) : 'Ingen pris'}
                            </span>
                          </div>
                        </div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {fields.map(field => {
                  const isFocusedRow = focusedFieldKey === field.key;
                  return (
                    <tr key={field.key} className={isFocusedRow ? 'bg-primary/5' : ''}>
                      <td className="sticky left-0 z-10 bg-background border-b border-r px-3 py-2 align-top min-w-[180px]">
                        <div className="font-medium text-foreground leading-tight">
                          {field.label}
                          {field.unit && (
                            <span className="text-muted-foreground font-normal ml-1">({field.unit})</span>
                          )}
                        </div>
                        {field.hint && (
                          <div className="text-[11px] text-muted-foreground mt-0.5 leading-snug">
                            {field.hint}
                          </div>
                        )}
                      </td>
                      {sortedRows.map(row => {
                        const isFocusedCell = isFocusedRow && row.variant.id === focusedVariantId;
                        const current = getCurrentValue(row, field);
                        const currentLabel = formatCurrent(current, field);
                        const cellValue = getCell(row.variant.id, field.key);

                        return (
                          <td
                            key={row.variant.id}
                            className={`border-b border-r px-2 py-2 align-top ${
                              row.variant.id === focusedVariantId ? 'bg-primary/[0.03]' : ''
                            } ${isFocusedCell ? 'ring-2 ring-primary/40 ring-inset' : ''}`}
                          >
                            <div className="flex flex-col gap-1">
                              <div className="text-[11px] text-muted-foreground">
                                Nu: {currentLabel}
                              </div>
                              {field.type === 'boolean' ? (
                                <div className="flex gap-1">
                                  <Button
                                    type="button"
                                    size="sm"
                                    variant={cellValue === 'ja' ? 'default' : 'outline'}
                                    className="flex-1 h-8 text-xs"
                                    onClick={() =>
                                      setCell(row.variant.id, field.key, cellValue === 'ja' ? '' : 'ja')
                                    }
                                  >
                                    Ja
                                  </Button>
                                  <Button
                                    type="button"
                                    size="sm"
                                    variant={cellValue === 'nej' ? 'default' : 'outline'}
                                    className="flex-1 h-8 text-xs"
                                    onClick={() =>
                                      setCell(row.variant.id, field.key, cellValue === 'nej' ? '' : 'nej')
                                    }
                                  >
                                    Nej
                                  </Button>
                                </div>
                              ) : field.type === 'select' && field.options ? (
                                <Select
                                  value={cellValue || undefined}
                                  onValueChange={v => setCell(row.variant.id, field.key, v)}
                                >
                                  <SelectTrigger className="h-8 text-xs">
                                    <SelectValue placeholder="Vælg…" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {field.options.map(opt => (
                                      <SelectItem key={opt.value} value={opt.value}>
                                        {opt.label}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              ) : (
                                <Input
                                  type={field.type === 'number' ? 'number' : 'text'}
                                  inputMode={field.type === 'number' ? 'decimal' : undefined}
                                  step="any"
                                  className="h-8 text-xs"
                                  value={cellValue}
                                  onChange={e => setCell(row.variant.id, field.key, e.target.value)}
                                  placeholder={current !== null && current !== undefined ? '' : '—'}
                                />
                              )}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Fælles felter + submit */}
        <div className="px-6 py-4 space-y-3 bg-background">
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Kilde-URL'er (anbefalet, op til {MAX_SOURCE_URLS})</Label>
              <div className="space-y-1.5">
                {sourceUrls.map((url, idx) => (
                  <div key={idx} className="flex items-center gap-1.5">
                    <Input
                      type="url"
                      value={url}
                      onChange={e => {
                        const next = [...sourceUrls];
                        next[idx] = e.target.value;
                        setSourceUrls(next);
                      }}
                      placeholder={idx === 0 ? 'https://producent.dk/specifikationer' : 'https://…'}
                      className="h-9"
                    />
                    {sourceUrls.length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-9 w-9 shrink-0"
                        onClick={() => setSourceUrls(sourceUrls.filter((_, i) => i !== idx))}
                        aria-label="Fjern kilde"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                ))}
                {sourceUrls.length < MAX_SOURCE_URLS && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-8 text-xs"
                    onClick={() => setSourceUrls([...sourceUrls, ''])}
                  >
                    <Plus className="w-3.5 h-3.5 mr-1" /> Tilføj kilde
                  </Button>
                )}
              </div>
            </div>
            <div>
              <Label htmlFor="mvs-notes">Note (valgfri)</Label>
              <Textarea
                id="mvs-notes"
                value={notes}
                onChange={e => setNotes(e.target.value)}
                placeholder="Yderligere kontekst…"
                rows={1}
                className="min-h-9"
              />
            </div>
          </div>

          {!user && (
            <div className="grid sm:grid-cols-2 gap-3 p-3 rounded-lg bg-muted">
              <div>
                <Label htmlFor="mvs-email">E-mail *</Label>
                <Input
                  id="mvs-email"
                  type="email"
                  value={submitterEmail}
                  onChange={e => setSubmitterEmail(e.target.value)}
                  placeholder="din@email.dk"
                  required
                  className="h-9"
                />
              </div>
              <div>
                <Label htmlFor="mvs-name">Navn (valgfri)</Label>
                <Input
                  id="mvs-name"
                  type="text"
                  value={submitterName}
                  onChange={e => setSubmitterName(e.target.value)}
                  placeholder="Dit navn"
                  className="h-9"
                />
              </div>
              <div className="hidden" aria-hidden="true">
                <Input
                  type="text"
                  value={honeypot}
                  onChange={e => setHoneypot(e.target.value)}
                  tabIndex={-1}
                  autoComplete="off"
                />
              </div>
            </div>
          )}

          <div className="flex items-center justify-between gap-3 pt-1">
            <p className="text-xs text-muted-foreground">
              {editedVariantCount > 0
                ? `Klar til at sende forslag for ${editedVariantCount} ${editedVariantCount === 1 ? 'variant' : 'varianter'}.`
                : 'Udfyld mindst én celle.'}
            </p>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
                Annullér
              </Button>
              <Button onClick={handleSubmit} disabled={submitting || editedVariantCount === 0}>
                {submitting && <Loader2 className="animate-spin mr-2 w-4 h-4" />}
                Indsend forslag
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
